import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { api, API_BASE, IDR, DATE, NUM, formatErr } from "@/lib/api";
import { canApprove, useAuth } from "@/lib/auth";

const COLORS = ["#059669", "#0891B2", "#0B4F78", "#F59E0B"];

export default function HppDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [calc, setCalc] = useState(null);
  const [product, setProduct] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/hpp/${id}`);
      setCalc(data);
      const { data: prods } = await api.get("/products");
      setProduct(prods.find((p) => p.id === data.product_id));
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!calc) return <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  const composition = [
    { label: "Bahan Baku", value: calc.cost_materials },
    { label: "Tenaga Kerja", value: calc.cost_labor },
    { label: "Overhead Variabel", value: calc.cost_overhead_variable },
    { label: "Overhead Tetap", value: calc.cost_overhead_fixed },
    { label: "Lingkungan", value: calc.cost_environmental },
  ].filter((c) => c.value > 0);

  const approve = async () => {
    try { await api.post(`/hpp/${id}/approve`); toast.success("HPP disetujui"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const downloadUrl = (fmt) => `${API_BASE}/reports/hpp/${id}/${fmt}`;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Perhitungan HPP</span>
          <h1 className="gc-h1 mt-1">{calc.calc_no}</h1>
          <p className="text-sm text-slate-500 mt-1">{calc.kind === "actual" ? "HPP Aktual" : "HPP Standar"} • {product?.name} • {DATE(calc.created_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={downloadUrl("pdf")} target="_blank" rel="noreferrer" className="gc-btn-ghost" data-testid="btn-download-pdf"><Download className="w-4 h-4" /> PDF</a>
          <a href={downloadUrl("xlsx")} target="_blank" rel="noreferrer" className="gc-btn-ghost" data-testid="btn-download-xlsx"><Download className="w-4 h-4" /> Excel</a>
          {canApprove(user) && calc.status !== "approved" && (
            <button onClick={approve} className="gc-btn-primary" data-testid="btn-approve-hpp"><CheckCircle2 className="w-4 h-4" /> Setujui</button>
          )}
          <Link to={`/app/pricing?hpp=${calc.id}`} className="gc-btn-primary" data-testid="btn-simulate-price"><TrendingUp className="w-4 h-4" /> Simulasi Harga Jual</Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="gc-card md:col-span-2">
          <span className="gc-label">Ringkasan Biaya</span>
          <table className="mt-3 w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              <Row label="Biaya Bahan Baku" value={calc.cost_materials} />
              <Row label="Biaya Tenaga Kerja" value={calc.cost_labor} />
              <Row label="Overhead Variabel" value={calc.cost_overhead_variable} />
              <Row label="Overhead Tetap" value={calc.cost_overhead_fixed} />
              <Row label="Biaya Lingkungan" value={calc.cost_environmental} />
              <Row label="Total Biaya Produksi" value={calc.total_cost} strong />
              <tr><td className="py-2 text-slate-500">Jumlah Produk Baik</td><td className="text-right font-semibold">{NUM(calc.good_qty, 0)}</td></tr>
              <tr><td className="py-2 text-slate-500">Jumlah Produk Gagal</td><td className="text-right font-semibold">{NUM(calc.reject_qty, 0)}</td></tr>
              <Row label="HPP per Unit" value={calc.hpp_per_unit} strong tone="emerald" />
            </tbody>
          </table>
        </div>
        <div className="gc-card">
          <span className="gc-label">Komposisi Biaya</span>
          <div className="h-64 mt-2">
            {composition.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={composition} dataKey="value" nameKey="label" innerRadius={40} outerRadius={80}>
                    {composition.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => IDR(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-slate-400 h-full flex items-center justify-center">Belum ada biaya.</div>}
          </div>
          <div className="mt-2 space-y-1 text-xs">
            {composition.map((c, i) => (
              <div key={c.label} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-slate-600"><span className="w-2 h-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{c.label}</span>
                <span className="font-semibold">{IDR(c.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {calc.variances && Object.keys(calc.variances).length > 0 && (
        <div className="gc-card">
          <span className="gc-label">Varians terhadap Standar</span>
          <table className="mt-3 w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              <Row label="HPP/Unit Standar" value={calc.variances.std_hpp_per_unit} />
              <Row label="HPP/Unit Aktual" value={calc.variances.actual_hpp_per_unit} />
              <Row label="Selisih per Unit" value={calc.variances.variance_per_unit} tone={calc.variances.variance_per_unit > 0 ? "rose" : "emerald"} />
              <Row label="Estimasi Biaya Produk Gagal" value={calc.variances.cost_of_reject_est} tone="amber" />
            </tbody>
          </table>
        </div>
      )}

      {calc.estimated_emissions_kg_co2 > 0 && (
        <div className="gc-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="gc-label !text-emerald-700">Estimasi Emisi CO₂</span>
              <h3 className="gc-h3 mt-1">Jejak Karbon Berbasis Faktor Emisi Bahan</h3>
            </div>
            <div className="text-[11px] rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 max-w-md text-amber-800">
              ⚠️ Estimasi berdasarkan faktor emisi yang dimasukkan pengguna pada tiap bahan — <strong>bukan hasil pengukuran laboratorium otomatis</strong>.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
              <div className="gc-label !text-emerald-700">Total Batch / Standar</div>
              <div className="font-cabinet text-3xl font-black text-emerald-700 mt-1">{NUM(calc.estimated_emissions_kg_co2, 3)} <span className="text-sm text-slate-500">kg CO₂eq</span></div>
            </div>
            <div className="rounded-2xl bg-slate-900 text-white p-4">
              <div className="gc-label !text-emerald-300">Per Unit</div>
              <div className="font-cabinet text-3xl font-black mt-1">{NUM(calc.emissions_per_unit_kg_co2, 4)} <span className="text-sm text-slate-400">kg CO₂eq</span></div>
            </div>
          </div>
          {calc.snapshot?.materials?.some((m) => m.emission_factor > 0) && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-3">Bahan</th>
                    <th className="text-right px-2">Qty</th>
                    <th className="text-right px-2">Faktor Emisi</th>
                    <th className="text-left px-2">Sumber</th>
                    <th className="text-right px-2">Emisi (kg CO₂eq)</th>
                  </tr>
                </thead>
                <tbody>
                  {calc.snapshot.materials.filter((m) => m.emission_factor > 0).map((m, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 pr-3">{m.material_name}</td>
                      <td className="px-2 text-right">{NUM(m.actual_qty ?? m.quantity, 3)}</td>
                      <td className="px-2 text-right">{NUM(m.emission_factor, 4)}</td>
                      <td className="px-2 text-xs text-slate-500 italic">{m.emission_source || "-"}</td>
                      <td className="px-2 text-right font-semibold text-emerald-700">{NUM(m.emissions_kg_co2, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {calc.snapshot?.materials?.length > 0 && (
        <div className="gc-card">
          <span className="gc-label">Rincian Bahan</span>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-3">Bahan</th>
                  <th className="text-right px-2">Qty</th>
                  <th className="text-left px-2">Satuan</th>
                  <th className="text-right px-2">Harga</th>
                  <th className="text-right px-2">Nilai</th>
                </tr>
              </thead>
              <tbody>
                {calc.snapshot.materials.map((m, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2 pr-3">{m.material_name || m.material_id}</td>
                    <td className="px-2 text-right">{NUM(m.actual_qty ?? m.quantity, 3)}</td>
                    <td className="px-2">{m.unit || "-"}</td>
                    <td className="px-2 text-right">{IDR(m.unit_price)}</td>
                    <td className="px-2 text-right font-semibold">{IDR(m.total_cost ?? m.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong, tone }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <tr className={tone ? toneMap[tone] : ""}>
      <td className={`py-2 ${strong ? "font-semibold" : "text-slate-500"}`}>{label}</td>
      <td className={`text-right ${strong ? "font-semibold" : ""}`}>{IDR(value)}</td>
    </tr>
  );
}
